// Connect to a store-owned endpoint returning Places API (New) Place Details JSON.
// API credentials belong on the server, never in theme settings or browser code.
if (!customElements.get('google-reviews-live')) customElements.define('google-reviews-live', class extends HTMLElement {
  connectedCallback() {
    if (!this.disclosureController) {
      this.disclosureController = new AbortController();
      const options = { signal: this.disclosureController.signal };
      this.language = this.dataset.language || (document.documentElement.lang || 'en').split('-')[0];
      this.dir = this.language === 'ar' ? 'rtl' : 'ltr';
      this.words = this.language === 'ar'
        ? { more: 'قراءة المزيد', less: 'عرض أقل', source: 'عرض على Google', count: 'مراجعة على Google', updated: 'آخر تحديث من Google:' }
        : this.language === 'fr'
          ? { more: 'Lire la suite', less: 'Réduire', source: 'Voir sur Google', count: 'avis Google', updated: 'Dernière mise à jour Google :' }
          : { more: 'Read more', less: 'Show less', source: 'View on Google', count: 'Google reviews', updated: 'Last updated from Google:' };
      this.cards = this.querySelector('[data-review-cards]');
      this.previous = this.querySelector('[data-review-previous]');
      this.next = this.querySelector('[data-review-next]');
      this.progress = this.querySelector('[data-review-progress] span');
      this.addEventListener('click', event => {
        const expand = event.target.closest('[data-review-expand]');
        if (expand) {
          const body = expand.closest('.google-review').querySelector('[data-review-body]');
          this.openReview(body, expand.closest('.google-review').querySelector('.review-card__name')?.textContent || '');
        }
        if (event.target.closest('[data-review-previous]')) this.scrollRail(-1);
        if (event.target.closest('[data-review-next]')) this.scrollRail(1);
      }, options);
      this.cards?.addEventListener('scroll', () => {
        cancelAnimationFrame(this.railFrame);
        this.railFrame = requestAnimationFrame(() => this.updateRail());
      }, { ...options, passive: true });
      this.addEventListener('error', event => {
        const image = event.target;
        if (!image.matches?.('img.review-card__avatar')) return;
        const name = image.closest('.google-review').querySelector('.review-card__name')?.textContent || '';
        const fallback = this.node('span', 'review-card__avatar', [...name.trim()][0] || '•');
        fallback.setAttribute('aria-hidden', 'true'); image.replaceWith(fallback);
      }, { ...options, capture: true });
      this.resize = new ResizeObserver(() => this.measure());
      const cards = this.querySelector('[data-review-cards]');
      if (cards) this.resize.observe(cards);
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('.google-review');
        if (card) card.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }, options);
      this.updateCards();
    }
    if (this.controller || this.dataset.source !== 'live' || !this.dataset.endpoint) return;
    try {
      this.endpoint = new URL(this.dataset.endpoint, location.href);
      if (this.endpoint.protocol !== 'https:' || this.endpoint.username || this.endpoint.password) return;
    } catch { return; }
    this.controller = new AbortController();
    this.interval = Math.max(5, Number(this.dataset.interval) || 15) * 60000;
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      if (this.visible) this.refresh(); else clearTimeout(this.timer);
    });
    this.observer.observe(this);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.refresh(); else clearTimeout(this.timer);
    }, { signal: this.controller.signal });
  }
  async refresh() {
    clearTimeout(this.timer);
    if (!this.visible || document.hidden || this.busy || !this.isConnected) return;
    const remaining = this.interval - (Date.now() - (this.lastAttempt || 0));
    if (remaining > 0) { this.timer = setTimeout(() => this.refresh(), remaining); return; }
    // Do not replace cards while someone is reading or interacting with them.
    if (this.contains(document.activeElement) || this.querySelector('.review-card__body.is-expanded')) {
      this.timer = setTimeout(() => this.refresh(), 30000); return;
    }
    this.busy = true; this.lastAttempt = Date.now();
    this.request = new AbortController();
    const timeout = setTimeout(() => this.request?.abort(), 10000);
    try {
      const response = await fetch(this.endpoint, { signal: this.request.signal, credentials: 'omit', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Reviews unavailable');
      const place = await response.json();
      if (!Number.isFinite(place.rating) || place.rating < 0 || place.rating > 5 || !Number.isInteger(place.userRatingCount) || place.userRatingCount < 0 || !Array.isArray(place.reviews)) throw new Error('Invalid review response');
      if (!this.isConnected) return;
      const track = this.querySelector('[data-review-cards]');
      const cards = place.reviews.map(review => this.card(review)).filter(Boolean);
      track.replaceChildren(...cards);
      this.querySelector('[data-review-rating]').textContent = place.rating;
      this.querySelector('[data-review-count]').textContent = place.userRatingCount + ' ' + this.words.count;
      const rating = this.querySelector('.google-reviews__rating'); rating.hidden = false;
      this.stars(place.rating, rating.querySelector('.review-stars'));
      const stamp = this.querySelector('[data-review-updated]'); stamp.hidden = false;
      stamp.textContent = this.words.updated + ' ' + new Intl.DateTimeFormat(this.language, { dateStyle: 'short', timeStyle: 'short' }).format(new Date());
      const providers = this.querySelector('[data-review-providers]'); providers.replaceChildren();
      (Array.isArray(place.attributions) ? place.attributions : []).forEach(p => { if (p.provider) providers.append(this.link(p.providerUri, p.provider)); });
      this.querySelector('[data-review-attribution]').hidden = false;
      this.updateCards();
      this.dataset.state = 'updated';
    } catch {
      // Keep the last verified snapshot and its date. Never claim a failed refresh succeeded.
      this.dataset.state = 'unavailable';
    } finally {
      clearTimeout(timeout); this.busy = false;
      if (this.isConnected && this.visible && !document.hidden) this.timer = setTimeout(() => this.refresh(), this.interval);
    }
  }
  updateCards() {
    const cards = [...this.querySelectorAll('[data-review-cards] > .google-review')];
    cards.forEach(card => { card.hidden = false; });
    this.measure();
    this.updateRail();
  }
  openReview(body, author) {
    if (!body?.textContent?.trim()) return;
    if (!this.textDialog) {
      const dialog = this.node('dialog', 'review-card-dialog');
      const bar = this.node('div', 'review-card-dialog__bar');
      const title = this.node('strong', 'review-card-dialog__title');
      const close = this.node('button', 'review-card-dialog__close', '×');
      close.type = 'button'; close.setAttribute('aria-label', this.language === 'ar' ? 'إغلاق' : this.language === 'fr' ? 'Fermer' : 'Close');
      close.addEventListener('click', () => dialog.close(), { signal: this.disclosureController.signal });
      bar.append(title, close);
      const text = this.node('p', 'review-card-dialog__body notranslate'); text.dir = 'auto'; text.setAttribute('translate', 'no');
      dialog.append(bar, text);
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); }, { signal: this.disclosureController.signal });
      this.append(dialog); this.textDialog = dialog;
    }
    this.textDialog.querySelector('.review-card-dialog__title').textContent = author;
    this.textDialog.querySelector('.review-card-dialog__body').textContent = body.textContent;
    this.textDialog.showModal();
  }
  railIndex() {
    const cards = [...this.querySelectorAll('[data-review-cards] > .google-review:not([hidden])')];
    if (!cards.length || !this.cards) return { cards, index: 0 };
    const box = this.cards.getBoundingClientRect(), rtl = getComputedStyle(this.cards).direction === 'rtl';
    let index = 0, distance = Infinity;
    cards.forEach((card, candidate) => { const rect = card.getBoundingClientRect(); const value = Math.abs(rtl ? box.right - rect.right : rect.left - box.left); if (value < distance) { distance = value; index = candidate; } });
    return { cards, index };
  }
  scrollRail(delta) {
    const { cards, index } = this.railIndex();
    cards[Math.max(0, Math.min(cards.length - 1, index + delta))]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }
  updateRail() {
    if (!this.cards || !this.progress || !this.previous || !this.next) return;
    const { cards, index } = this.railIndex(), canMove = this.cards.scrollWidth > this.cards.clientWidth + 2;
    this.previous.hidden = this.next.hidden = !canMove; this.previous.disabled = index <= 0; this.next.disabled = index >= cards.length - 1;
    this.progress.style.setProperty('--review-progress', cards.length ? `${((index + 1) / cards.length) * 100}%` : '0%');
    this.progress.parentElement.hidden = !canMove;
  }
  measure() {
    this.querySelectorAll('[data-review-body]').forEach(body => {
      if (!body.clientHeight || body.closest('[hidden]')) return;
      const expand = body.closest('.google-review').querySelector('[data-review-expand]');
      if (expand) expand.hidden = !body.classList.contains('is-expanded') && body.scrollHeight <= body.clientHeight + 2;
    });
  }
  node(tag, className, text) { const el = document.createElement(tag); el.className = className; if (text !== undefined) el.textContent = text; return el; }
  safeURL(value) { try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password ? url.href : ''; } catch { return ''; } }
  link(url, text, className = '') {
    const safe = this.safeURL(url), el = this.node(safe ? 'a' : 'span', className, text);
    if (safe) { el.href = safe; el.target = '_blank'; el.rel = 'noopener'; }
    return el;
  }
  stars(rating, el = this.node('span', 'review-stars')) {
    el.setAttribute('role', 'img'); el.setAttribute('aria-label', rating + ' / 5');
    el.style.setProperty('--rating-fill', rating * 20 + '%');
    if (!el.children.length) for (const name of ['base', 'fill']) { const span = this.node('span', 'review-stars__' + name, '★★★★★'); span.setAttribute('aria-hidden', 'true'); el.append(span); }
    return el;
  }
  card(review) {
    const author = review.authorAttribution;
    if (!author?.displayName || !Number.isFinite(review.rating) || review.rating < 1 || review.rating > 5) return null;
    const card = this.node('li', 'google-review review-card'), heading = this.node('div', 'review-card__header');
    const photo = this.safeURL(author.photoUri);
    const avatar = this.node(photo ? 'img' : 'span', 'review-card__avatar', photo ? undefined : [...author.displayName.trim()][0] || '•');
    if (photo) { avatar.referrerPolicy = 'no-referrer'; avatar.src = photo; avatar.alt = ''; avatar.loading = 'lazy'; avatar.decoding = 'async'; avatar.width = 30; avatar.height = 30; }
    else avatar.setAttribute('aria-hidden', 'true');
    heading.append(avatar);
    const byline = this.node('div', 'review-card__person');
    const name = this.link(author.uri, author.displayName, 'review-card__name notranslate');
    name.dir = 'auto'; name.setAttribute('translate', 'no'); name.title = author.displayName;
    byline.append(name);
    const meta = this.node('div', 'review-card__meta'); meta.append(this.stars(review.rating, this.node('span', 'review-card__stars')));
    if (review.relativePublishTimeDescription) { const date = this.node('span', 'review-card__date', review.relativePublishTimeDescription); date.dir = 'auto'; meta.append(date); }
    byline.append(meta);
    const platform = this.node('span', 'review-card__platform'); platform.dataset.platform = 'google'; platform.setAttribute('role', 'img'); platform.setAttribute('aria-label', 'Google');
    const logo = this.node('img', ''); logo.src = this.querySelector('.google-reviews__score img')?.src || ''; logo.width = logo.height = 18; logo.alt = ''; logo.loading = 'lazy'; platform.append(logo);
    heading.append(byline, platform); card.append(heading);
    const footer = this.node('div', 'review-card__footer');
    if (review.originalText?.text || review.text?.text) {
      const quote = this.node('blockquote', 'review-card__body notranslate', review.originalText?.text || review.text.text);
      quote.dir = 'auto'; quote.setAttribute('translate', 'no'); quote.dataset.reviewBody = '';
      const language = review.originalText?.languageCode || review.text?.languageCode;
      if (language) quote.lang = language;
      card.append(quote);
      const expand = this.node('button', 'review-card__expand', this.words.more); expand.type = 'button'; expand.dataset.reviewExpand = ''; expand.setAttribute('aria-expanded', 'false'); expand.hidden = true; footer.append(expand);
    }
    const source = this.safeURL(review.googleMapsUri);
    if (source) { const link = this.link(source, this.words.source, 'review-card__source'); const arrow = this.node('span', 'review-card__source-arrow', '↗'); arrow.setAttribute('aria-hidden', 'true'); link.append(arrow); footer.append(link); }
    card.append(footer);
    return card;
  }
  disconnectedCallback() { this.disclosureController?.abort(); this.disclosureController = null; this.controller?.abort(); this.controller = null; this.request?.abort(); this.observer?.disconnect(); this.resize?.disconnect(); cancelAnimationFrame(this.railFrame); clearTimeout(this.timer); }
});
